// copied from the end of https://www.newline.co/@3nvi/centralizing-api-error-handling-in-react-apps--80296494
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import ErrorComponent from "../../components/SimpleComponents/Errors/ErrorComponent";
import ErrorBoundary from "../../components/SimpleComponents/Errors/ErrorBoundary";
import { useAuth0 } from "../AuthManager/Auth";
import { useReload } from "utils/customHooks/useReload";
import { handleError } from "./handleError";
import {
  ALLOCATION_LIST_QUERY_TITLE,
  DATA_DUMP_FOR_HUBLET_QUERY_TITLE,
  DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE,
  DATA_DUMP_SINGLE_QUERY_TITLE,
  BATCH_PROGRESSION_QUERY_TITLE,
  DATA_DUMP_ROW_TARGET_TREND_LINE_TITLE,
  DATA_DUMP_ROW_SINGLE_TIMELINE_TITLE,
  HeadersType,
  PROCESS_GROUPS_QUERY_TITLE,
  resetAllRetriesFromLocalStorage,
} from "./errorManagerUtils";
import * as Sentry from "@sentry/react";
import { ApolloError, ServerParseError } from "@apollo/client";
import { useServiceStatus } from "components/SimpleComponents/TopBanner/useServiceStatus";
import { useTraceToHoneycomb } from "tracer";
import {
  TOO_MANY_RECENT_FAILURES_BREAKER_OPEN,
  getCkeyFromWindow,
} from "constants/constants";
import { FORCE_MIDGARD_BREAKER_OPEN } from "components/DataGetter/ForceMidgardBreakerOpenOnce";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useAtom } from "jotai";
import { errorOverlayStateAtom } from "reports/components/ErrorOverlay";

// Test Cases:

// 1. log out log in normally
// 2. delete id token from localstorage
// 3. modify id token in localstorage
// 4. add fake param to DataDumpAggregateQuery in MidgardProvider to force 400 error

/**
 * Renders full-page error components if the history.location.state contains an errorStatusCode
 * (history.location.state is set by useQuery / handleError / useHandleError)
 */
const ErrorManager = ({ children }) => {
  const location = useLocation();
  const reload = useReload();

  const { getAccessTokenSilently } = useAuth0();

  const errorStatusCode = (location.state as any)?.errorStatusCode;
  const errorMessage = (location.state as any)?.errorMessage;
  const errorMoreInfo = (location.state as any)?.errorMoreInfo;

  const defaultProps = {
    escapeRoute: "/",
    escapeRouteName: "main page",
    errorMessage,
    isFullWidth: true,
    errorMoreInfo,
  };

  // Sentry: attach extra context to the error
  // https://docs.sentry.io/platforms/javascript/enriching-events/context/#passing-context-directly
  if (errorStatusCode || errorMessage || errorMoreInfo) {
    console.warn("🕵️‍♀️", { errorStatusCode, errorMessage, errorMoreInfo });
    Sentry.captureException(new Error(`ERROR_CODE_${errorStatusCode}`), {
      tags: { errorStatusCode },
      extra: {
        errorMessage,
        errorMoreInfo,
      },
    });
  }

  // handle
  switch (errorStatusCode) {
    case 404:
      // * this should never appear since we redirect to "/" by default in handleError
      return (
        <ErrorComponent
          {...defaultProps}
          onRetry={() => {
            resetAllRetriesFromLocalStorage();
            reload();
          }}
          errorTitle="404: Not Found"
        />
      );
    case 400:
      return (
        <ErrorComponent
          {...defaultProps}
          onRetry={() => {
            resetAllRetriesFromLocalStorage();
            getAccessTokenSilently();
            reload();
          }}
          errorTitle="400: Bad Request"
          errorSubtitle="A malformed request was sent to the server. Maybe your authentication expired?"
        />
      );
    // ... cases for other types of errors

    case Boolean(errorStatusCode):
      console.warn(
        `💫😵 Error was ignored in ErrorManager: ${errorStatusCode}: ${errorMessage}`
      );
      return (
        <ErrorBoundary id={`ErrorManager_${errorStatusCode}`}>
          {children}
        </ErrorBoundary>
      );

    default:
      return <ErrorBoundary id="ErrorManager">{children}</ErrorBoundary>;
  }
};

/**
 * Handles errors based on the type of error;
 * for most errors, populates the history.location.state
 * with error info to be handled by ErrorManager
 *
 * useHandleError returns handleError(err, headers)
 */
export function useHandleError(): (
  err: ApolloError,
  headers: HeadersType
) => void {
  const navigate = useNavigate();
  const reload = useReload();
  const { isLoading: auth0Loading, user, getAccessTokenSilently } = useAuth0();
  const [, setErrorOverlayState] = useAtom(errorOverlayStateAtom);

  const [, setServicesStatus] = useServiceStatus();
  const isStorybook = useIsStorybook();
  const traceToHoneycomb = useTraceToHoneycomb({
    sampleRateOverride: isStorybook ? 0 : 1,
  });

  return useCallback(
    (err, headers = {}) => {
      // detect degraded status for midgard errors here
      const isMidgardQuery = QUERY_NAMES_FOR_STATUS_CHECK.midgard.includes(
        headers.queryName
      );
      if (isMidgardQuery) {
        if (isCriticalMidgardError(err)) {
          // start a timer to show the error overlay, in case it recovers quickly
          const showBannerTimer = window.setTimeout(() => {
            // open the breaker
            console.log(
              "🔁💥 Critical Midgard error, opening ServiceOutageIndicator + MidgardStatusChecker"
            );
            setServicesStatus(prev => ({
              ...prev,
              midgard: {
                ...prev.midgard,
                breakerOpen: true,
                degraded: true,
                showBannerTimer: null,
                latestError: err,
              },
            }));
          }, MIDGARD_FAST_RECOVERY_TIME_BEFORE_SHOW_BANNER);

          setServicesStatus(prev => ({
            ...prev,
            midgard: { ...prev.midgard, showBannerTimer },
          }));

          console.log(err);
          setServicesStatus(prev => ({
            ...prev,
            midgard: {
              ...prev.midgard,
              breakerOpen: true,
              degraded: true,
              latestError: err,
            },
          }));
          // trace to honeycomb
          traceToHoneycomb({
            dataToTrace: {
              name: "Flight Midgard Breaker Open",
              email: String(user?.email),
              ckey: getCkeyFromWindow(),
              latestError: JSON.stringify(err, null, 2),
              url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          });
        } else if (isKnownMidgardError(err)) {
          // trace to honeycomb
          traceToHoneycomb({
            dataToTrace: {
              name: "Flight Midgard Known Bug",
              email: String(user?.email),
              ckey: getCkeyFromWindow(),
              latestError: JSON.stringify(err, null, 2),
              url: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          });
          // open the error indicator (not the breaker)
          setServicesStatus(prev => ({
            ...prev,
            midgard: {
              ...prev.midgard,
              degraded: true,
              latestError: err,
            },
          }));
        }
      }

      // detect "too many segments" error and display overlay
      if (err.message.includes("too many segments")) {
        setErrorOverlayState({
          error: true,
          message: err.message,
        });
        return;
      }

      return handleError({
        err,
        navigate,
        getAccessTokenSilently,
        auth0Loading,
        reload,
        user,
        headers,
      });
    },
    [
      navigate,
      getAccessTokenSilently,
      auth0Loading,
      reload,
      user,
      setServicesStatus,
      traceToHoneycomb,
      setErrorOverlayState,
    ]
  );
}

export const MIDGARD_FAST_RECOVERY_TIME_BEFORE_SHOW_BANNER = 5 * 1000;

export default ErrorManager;

function isCriticalMidgardError(err: ApolloError) {
  // detect midgard TooManyRecentFailuresBreakerOpen
  const isTooManyRecentFailuresBreakerOpen = JSON.stringify(err).includes(
    TOO_MANY_RECENT_FAILURES_BREAKER_OPEN
  );
  const isCritical =
    isTooManyRecentFailuresBreakerOpen ||
    // 500 Internal Server Error
    err.message.includes("500") ||
    err.graphQLErrors.some(e => e.message.includes("500")) ||
    (err.networkError as ServerParseError)?.statusCode === 500 ||
    // 502 Bad Gateway
    err.message.includes("502") ||
    err.graphQLErrors.some(e => e.message.includes("502")) ||
    (err.networkError as ServerParseError)?.statusCode === 502 ||
    // 503 Service Unavailable
    err.message.includes("503") ||
    err.graphQLErrors.some(e => e.message.includes("503")) ||
    (err.networkError as ServerParseError)?.statusCode === 503;
  return isCritical;
}

function isKnownMidgardError(err: ApolloError) {
  const isKnown =
    // division by zero
    err.message.includes("division by zero") ||
    err.graphQLErrors.some(e => e.message.includes("division by zero"));
  console.log("isKnownMidgardError ~ err", err);
  console.log("isKnownMidgardError ~ isKnown", isKnown);
  return isKnown;
}

const QUERY_NAMES_FOR_STATUS_CHECK: { midgard: string[] } = {
  midgard: [
    "UPDATE_LOT_START_DATA_DUMP_FOR_PROCESSES",
    "UPDATE_GROUP_TILE_DATA_DUMP_FOR_PROCESSES",
    "UPDATE_DATA_DUMP_FOR_PROCESS",
    "UPDATE_FULL_RANGE_DATA_DUMP_FOR_PROCESSES",
    "UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES",
    "UPDATE_LAST_DATA_DUMP_ROW",
    "UPDATE_LAST_DATA_DUMP_ROW_PER_PROCESS",
    "UPDATE_DATA_DUMP_PNL_LOSSES_FOR_PROCESSES",
    "UPDATE_FULL_RANGE_DATA_DUMP_FOR_SUBORDINATE_PROCESSES",
    "UPDATE_CUSTOM_DATA_DUMP_FOR_PROCESSES",
    PROCESS_GROUPS_QUERY_TITLE,
    ALLOCATION_LIST_QUERY_TITLE,
    DATA_DUMP_FOR_HUBLET_QUERY_TITLE,
    DATA_DUMP_FOR_FORECASTED_LOT_COMPLETION_TITLE,
    DATA_DUMP_SINGLE_QUERY_TITLE,
    BATCH_PROGRESSION_QUERY_TITLE,
    DATA_DUMP_ROW_TARGET_TREND_LINE_TITLE,
    DATA_DUMP_ROW_SINGLE_TIMELINE_TITLE,
    FORCE_MIDGARD_BREAKER_OPEN,
  ],
};
